import { useIdentifiedChoiceResults } from '@mentimeter/presentation-data-hooks/results/identified/choices';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import type { ChoicesT } from '@mentimeter/question-modules-types';
import type { MultipleChoiceStaticContent } from '@core-api/presentation/types/response';
import { ChoicesPresentation } from './ChoicesPresentation';
import { TrendsDataHandler } from './TrendsDataHandler';
import type { Choice } from './types';

// controls have the same type, copy pasted for now
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type SlideStateChoices = { reveal?: boolean };

export function ChoicesPresentationDataHandler() {
  const {
    useQuestion,
    useSeries,
    useTrends,
    useSegmentation,
    useConfig,
    useSlideState,
    useResults,
    useCurrentSession,
    InteractiveCanvasComponents,
  } = usePresentationContext();
  const slide = useQuestion();
  const { participation_identity_mode, participation_identity_visibility } =
    useSeries();
  const { showTrends: shouldShowTrends } = useTrends();
  const {
    isSegmented,
    segmentedQuestionTitle = '',
    segments = [],
  } = useSegmentation();

  const { mode, enableAnimations } = useConfig();
  const [{ reveal }] = useSlideState<SlideStateChoices>();

  const staticContent = slide.staticContent as MultipleChoiceStaticContent;
  const interactiveContent = slide.interactiveContents[0];

  const shouldShowCorrect =
    interactiveContent?.correctAnswerMode === 'enabled' &&
    (reveal || ['screenshot', 'public', 'idle'].includes(mode));

  const results = useResults<ChoicesT>();
  const { isCurrentSessionActive } = useCurrentSession();

  const isSubTypeWithIdentifiedResponses =
    staticContent.interactiveContentStyling.visualization !== 'dots';

  const hasIdentifiedResultsSettingOnAndVisible =
    participation_identity_mode === 'identified' &&
    participation_identity_visibility === 'everyone';

  const shouldFetchIdentifiedResults =
    hasIdentifiedResultsSettingOnAndVisible &&
    isSubTypeWithIdentifiedResponses &&
    isCurrentSessionActive;

  const { data, isLoading } = useIdentifiedChoiceResults(
    slide.slideAdminKey,
    shouldFetchIdentifiedResults,
  );
  const shouldShowIdentifiedResults =
    Boolean(data || isLoading) && slide.responseVisibility === 'visible';

  const choices: Choice[] = slide.interactiveContents[0]!.choices.map(
    (choice, index) => ({
      id: choice.legacyChoiceId ?? index + 1,
      label: choice.title,
      imageUrl: choice.image?.presets.original.url,
      isMarkedCorrect: choice.markedCorrect,
    }),
  );

  return (
    <ChoicesPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={slide.interactiveContents[0]!.description}
      fontSizeOffset={staticContent.textSizeOffset}
      identifiedResults={
        shouldShowIdentifiedResults ? (data?.results ?? []) : null
      }
      segmentation={{
        isSegmented,
        segmentedQuestionTitle,
        segments,
      }}
      shouldShowCorrect={shouldShowCorrect}
      results={results}
      choices={choices}
      visualizationType={staticContent.interactiveContentStyling.visualization}
      shouldHideResults={slide.responseVisibility === 'hidden'}
      shouldShowPercent={
        staticContent.interactiveContentStyling.responseCountDisplayMode ===
        'percentage'
      }
      shouldAnimate={enableAnimations}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
    >
      {shouldShowTrends && (
        <TrendsDataHandler
          choices={choices}
          shouldHideResults={slide.responseVisibility === 'hidden'}
        />
      )}
    </ChoicesPresentation>
  );
}
